import {
  CalendarDays,
  CalendarRange,
  ChevronDown,
  Download,
  GraduationCap,
  Info,
  Pencil,
  Plus,
  Save,
  Trash2,
  UserRound,
  Users,
  X,
} from "lucide-react";
import { FormEvent, useCallback, useEffect, useState } from "react";

import api from "../api/client";
import type {
  Paginated,
  School,
  SchoolChild,
  SchoolGroup,
  SchoolHoliday,
  SchoolVacation,
  SchoolStudyDay,
} from "../types/api";
import { unwrapResults } from "../types/api";
import { apiErrorMessage } from "../utils/apiError";
import { formatDate } from "../utils/format";
import { ErrorNotice, LoadingState, SuccessNotice } from "./Feedback";
import DateField from "./DateField";

type SchoolDraft = {
  id: number | null;
  name: string;
};

type HolidayDraft = {
  id: number | null;
  school: number;
  name: string;
  start_date: string;
  end_date: string;
};

type GroupDraft = { id: number | null; school: number; name: string };

type ChildDraft = {
  id: number | null;
  school: number;
  group: number | null;
  new_group_name: string;
  name: string;
};

type StudyDayDraft = {
  id: number | null;
  school: number;
  name: string;
  date: string;
  scope: SchoolStudyDay["scope"];
  part: SchoolStudyDay["part"];
  groups: number[];
};

const today = new Date().toISOString().slice(0, 10);
const currentSchoolYear = `${new Date().getFullYear()}/${new Date().getFullYear() + 1}`;
const nextSchoolYear = `${new Date().getFullYear() + 1}/${new Date().getFullYear() + 2}`;

type SchoolVacationYearsResponse = { school_years: string[] };

export default function SchoolVacationsSettings() {
  const [schools, setSchools] = useState<School[]>([]);
  const [holidays, setHolidays] = useState<SchoolHoliday[]>([]);
  const [groups, setGroups] = useState<SchoolGroup[]>([]);
  const [children, setChildren] = useState<SchoolChild[]>([]);
  const [studyDays, setStudyDays] = useState<SchoolStudyDay[]>([]);
  const [templateYears, setTemplateYears] = useState<string[]>([]);
  const [schoolDraft, setSchoolDraft] = useState<SchoolDraft | null>(null);
  const [templateSchoolId, setTemplateSchoolId] = useState<number | null>(null);
  const [templateRegion, setTemplateRegion] = useState<
    NonNullable<School["region"]> | ""
  >("");
  const [templateSchoolYear, setTemplateSchoolYear] = useState(currentSchoolYear);
  const [holidayDraft, setHolidayDraft] = useState<HolidayDraft | null>(null);
  const [groupDraft, setGroupDraft] = useState<GroupDraft | null>(null);
  const [childDraft, setChildDraft] = useState<ChildDraft | null>(null);
  const [studyDayDraft, setStudyDayDraft] = useState<StudyDayDraft | null>(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [
        schoolResponse,
        holidayResponse,
        groupResponse,
        childResponse,
        dayResponse,
        schoolVacationResponse,
        schoolVacationYearsResponse,
      ] = await Promise.all([
        api.get<Paginated<School> | School[]>("/core/schools/?page_size=200"),
        api.get<Paginated<SchoolHoliday> | SchoolHoliday[]>(
          "/core/school-holidays/?page_size=1000",
        ),
        api.get<Paginated<SchoolGroup> | SchoolGroup[]>(
          "/core/school-groups/?page_size=1000",
        ),
        api.get<Paginated<SchoolChild> | SchoolChild[]>(
          "/core/school-children/?page_size=1000",
        ),
        api.get<Paginated<SchoolStudyDay> | SchoolStudyDay[]>(
          "/core/school-study-days/?page_size=1000",
        ),
        api.get<Paginated<SchoolVacation> | SchoolVacation[]>(
          "/core/school-vacations/?page_size=1000",
        ),
        api
          .get<SchoolVacationYearsResponse>("/core/school-vacations/years/")
          .catch(() => null),
      ]);
      setSchools(unwrapResults(schoolResponse.data));
      setHolidays(unwrapResults(holidayResponse.data));
      setGroups(unwrapResults(groupResponse.data));
      setChildren(unwrapResults(childResponse.data));
      setStudyDays(unwrapResults(dayResponse.data));
      const schoolVacations = unwrapResults(schoolVacationResponse.data);
      setTemplateYears(
        schoolVacationYearsResponse?.data.school_years ??
          uniqueSchoolYears(schoolVacations),
      );
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(requestError, "De schoolvakanties konden niet worden geladen."),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const closeEditors = () => {
    setSchoolDraft(null);
    setHolidayDraft(null);
    setGroupDraft(null);
    setChildDraft(null);
    setStudyDayDraft(null);
    setError("");
  };

  const startSchoolEdit = (school?: School) => {
    closeEditors();
    setSchoolDraft(
      school
        ? {
            id: school.id,
            name: school.name,
          }
        : { id: null, name: "" },
    );
    setSuccess("");
  };

  const saveSchool = async (event: FormEvent) => {
    event.preventDefault();
    if (!schoolDraft) return;
    setWorking(true);
    setError("");
    try {
      const payload = { name: schoolDraft.name };
      let schoolId = schoolDraft.id;
      if (schoolId) {
        await api.patch(`/core/schools/${schoolId}/`, payload);
      } else {
        const response = await api.post<School>("/core/schools/", payload);
        schoolId = response.data.id;
      }
      setSchoolDraft(null);
      setSuccess("School opgeslagen.");
      await load();
    } catch (requestError: unknown) {
      setError(apiErrorMessage(requestError, "De school kon niet worden opgeslagen."));
    } finally {
      setWorking(false);
    }
  };

  const applyVacationTemplate = async (school: School) => {
    if (!templateSchoolYear) {
      setError("Kies eerst een schooljaar.");
      return;
    }
    if (!templateRegion) {
      setError("Kies eerst een regio.");
      return;
    }
    setWorking(true);
    setError("");
    try {
      const response = await api.post<{ created_count: number }>(
        `/core/schools/${school.id}/apply_template/`,
        { region: templateRegion, school_year: templateSchoolYear },
      );
      setTemplateSchoolId(null);
      setTemplateRegion("");
      setSuccess(
        `${response.data.created_count} vakantie(s) uit schooljaar ${templateSchoolYear} en regio ${regionLabel(
          templateRegion,
        )} overgenomen.`,
      );
      await load();
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(
          requestError,
          "De vakantietemplate kon niet worden overgenomen.",
        ),
      );
    } finally {
      setWorking(false);
    }
  };

  const removeSchool = async (school: School) => {
    if (!window.confirm(`School ${school.name} en alle gegevens verwijderen?`)) return;
    try {
      await api.delete(`/core/schools/${school.id}/`);
      setSuccess("School verwijderd.");
      await load();
    } catch (requestError: unknown) {
      setError(apiErrorMessage(requestError, "De school kon niet worden verwijderd."));
    }
  };

  const startHolidayEdit = (school: School, holiday?: SchoolHoliday) => {
    closeEditors();
    setHolidayDraft(
      holiday
        ? {
            id: holiday.id,
            school: holiday.school,
            name: holiday.name,
            start_date: holiday.start_date,
            end_date: holiday.end_date,
          }
        : {
            id: null,
            school: school.id,
            name: "",
            start_date: today,
            end_date: today,
          },
    );
    setSuccess("");
  };

  const saveHoliday = async (event: FormEvent) => {
    event.preventDefault();
    if (!holidayDraft) return;
    setWorking(true);
    setError("");
    try {
      if (holidayDraft.id) {
        await api.patch(`/core/school-holidays/${holidayDraft.id}/`, holidayDraft);
      } else {
        await api.post("/core/school-holidays/", holidayDraft);
      }
      setHolidayDraft(null);
      setSuccess("Vakantie opgeslagen.");
      await load();
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(requestError, "De vakantie kon niet worden opgeslagen."),
      );
    } finally {
      setWorking(false);
    }
  };

  const removeHoliday = async (holiday: SchoolHoliday) => {
    if (!window.confirm(`${holiday.name} verwijderen?`)) return;
    try {
      await api.delete(`/core/school-holidays/${holiday.id}/`);
      setSuccess("Vakantie verwijderd.");
      await load();
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(requestError, "De vakantie kon niet worden verwijderd."),
      );
    }
  };

  const startGroupEdit = (school: School) => {
    closeEditors();
    setGroupDraft({ id: null, school: school.id, name: "" });
    setSuccess("");
  };

  const saveGroup = async (event: FormEvent) => {
    event.preventDefault();
    if (!groupDraft) return;
    setWorking(true);
    setError("");
    try {
      await api.post("/core/school-groups/", groupDraft);
      setGroupDraft(null);
      setSuccess("Groep toegevoegd.");
      await load();
    } catch (requestError: unknown) {
      setError(apiErrorMessage(requestError, "De groep kon niet worden toegevoegd."));
    } finally {
      setWorking(false);
    }
  };

  const removeGroup = async (group: SchoolGroup) => {
    if (!window.confirm(`Groep ${group.name} verwijderen?`)) return;
    try {
      await api.delete(`/core/school-groups/${group.id}/`);
      setSuccess("Groep verwijderd.");
      await load();
    } catch (requestError: unknown) {
      setError(apiErrorMessage(requestError, "De groep kon niet worden verwijderd."));
    }
  };

  const startChildEdit = (school: School) => {
    closeEditors();
    setChildDraft({
      id: null,
      school: school.id,
      group: null,
      new_group_name: "",
      name: "",
    });
    setSuccess("");
  };

  const saveChild = async (event: FormEvent) => {
    event.preventDefault();
    if (!childDraft) return;
    setWorking(true);
    setError("");
    try {
      let groupId = childDraft.group;
      if (!groupId && childDraft.new_group_name.trim()) {
        const response = await api.post<SchoolGroup>("/core/school-groups/", {
          school: childDraft.school,
          name: childDraft.new_group_name.trim(),
        });
        groupId = response.data.id;
      }
      if (!groupId) {
        setError("Kies een groep of maak een nieuwe groep aan.");
        setWorking(false);
        return;
      }
      await api.post("/core/school-children/", {
        school: childDraft.school,
        group: groupId,
        name: childDraft.name,
      });
      setChildDraft(null);
      setSuccess("Kind toegevoegd.");
      await load();
    } catch (requestError: unknown) {
      setError(apiErrorMessage(requestError, "Het kind kon niet worden toegevoegd."));
    } finally {
      setWorking(false);
    }
  };

  const removeChild = async (child: SchoolChild) => {
    if (!window.confirm(`${child.name} verwijderen?`)) return;
    try {
      await api.delete(`/core/school-children/${child.id}/`);
      setSuccess("Kind verwijderd.");
      await load();
    } catch (requestError: unknown) {
      setError(apiErrorMessage(requestError, "Het kind kon niet worden verwijderd."));
    }
  };

  const startStudyDayEdit = (school: School, studyDay?: SchoolStudyDay) => {
    closeEditors();
    setStudyDayDraft(
      studyDay
        ? {
            id: studyDay.id,
            school: studyDay.school,
            name: studyDay.name,
            date: studyDay.date,
            scope: studyDay.scope,
            part: studyDay.part,
            groups: [...studyDay.groups],
          }
        : {
            id: null,
            school: school.id,
            name: "",
            date: today,
            scope: "whole_school",
            part: "whole_day",
            groups: [],
          },
    );
    setSuccess("");
  };

  const saveStudyDay = async (event: FormEvent) => {
    event.preventDefault();
    if (!studyDayDraft) return;
    setWorking(true);
    setError("");
    try {
      const payload = {
        ...studyDayDraft,
        groups: studyDayDraft.scope === "whole_school" ? [] : studyDayDraft.groups,
      };
      if (studyDayDraft.id) {
        await api.patch(`/core/school-study-days/${studyDayDraft.id}/`, payload);
      } else {
        await api.post("/core/school-study-days/", payload);
      }
      setStudyDayDraft(null);
      setSuccess("Studiedag opgeslagen.");
      await load();
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(requestError, "De studiedag kon niet worden opgeslagen."),
      );
    } finally {
      setWorking(false);
    }
  };

  const removeStudyDay = async (studyDay: SchoolStudyDay) => {
    if (!window.confirm(`${studyDay.name} verwijderen?`)) return;
    try {
      await api.delete(`/core/school-study-days/${studyDay.id}/`);
      setSuccess("Studiedag verwijderd.");
      await load();
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(requestError, "De studiedag kon niet worden verwijderd."),
      );
    }
  };

  const toggleStudyDayGroup = (groupId: number) => {
    if (!studyDayDraft) return;
    setStudyDayDraft({
      ...studyDayDraft,
      groups: studyDayDraft.groups.includes(groupId)
        ? studyDayDraft.groups.filter((id) => id !== groupId)
        : [...studyDayDraft.groups, groupId],
    });
  };

  const schoolYearOptions = Array.from(
    new Set([...templateYears, currentSchoolYear, nextSchoolYear]),
  ).sort((left, right) => right.localeCompare(left));

  if (loading) return <LoadingState label="Schoolvakanties laden..." />;

  return (
    <>
      {error && <ErrorNotice>{error}</ErrorNotice>}
      {success && <SuccessNotice>{success}</SuccessNotice>}

      <section className="surface settings-section school-vacations-workspace">
        <div className="settings-heading settings-heading-with-action">
          <div>
            <h2>Schoolvakanties</h2>
            <p>
              Beheer scholen, groepen, kinderen, vakanties en studiedagen voor je
              persoonlijke planning.
            </p>
          </div>
          <button
            className="button subtle"
            type="button"
            onClick={() => startSchoolEdit()}
          >
            <Plus size={16} /> School toevoegen
          </button>
        </div>
        <div className="notice notice-info compact school-vacations-source" role="note">
          <Info size={16} />
          <div>
            <strong>Officiële bron van de regiotemplates</strong>
            <p>
              De landelijke schoolvakanties worden opgehaald bij de{" "}
              <a
                href="https://www.rijksoverheid.nl/opendata/schoolvakanties"
                target="_blank"
                rel="noreferrer"
              >
                Rijksoverheid
              </a>{" "}
              via de{" "}
              <a
                href="https://opendata.rijksoverheid.nl/v1/infotypes/schoolholidays"
                target="_blank"
                rel="noreferrer"
              >
                officiële JSON API
              </a>
              .
            </p>
            <p>
              Let op: sommige perioden zijn adviesdata. Scholen kunnen individueel
              afwijken; controleer daarom altijd de vakantiedagen bij de school.
            </p>
          </div>
        </div>

        {schoolDraft && (
          <div className="modal-backdrop school-vacations-modal" role="presentation">
            <form
              className="modal-panel school-vacations-editor"
              role="dialog"
              aria-modal="true"
              aria-labelledby="school-editor-title"
              onSubmit={saveSchool}
            >
              <div className="settings-heading">
                <h2 id="school-editor-title">
                  {schoolDraft.id ? "School wijzigen" : "School toevoegen"}
                </h2>
              </div>
              <div className="form-row">
                <label>
                  <span>School</span>
                  <input
                    value={schoolDraft.name}
                    onChange={(event) =>
                      setSchoolDraft({ ...schoolDraft, name: event.target.value })
                    }
                    required
                  />
                </label>
              </div>
              <div className="form-actions">
                <button className="button primary" type="submit" disabled={working}>
                  <Save size={16} /> Opslaan
                </button>
                <button
                  className="button subtle"
                  type="button"
                  onClick={() => setSchoolDraft(null)}
                >
                  <X size={16} /> Annuleren
                </button>
              </div>
            </form>
          </div>
        )}

        {schools.length === 0 && !schoolDraft ? (
          <div className="empty-inline">
            <GraduationCap size={22} />
            <span>Nog geen scholen. Voeg een school toe om te starten.</span>
          </div>
        ) : (
          <div className="school-vacations-list">
            {schools.map((school) => {
              const schoolHolidays = holidays.filter(
                (holiday) => holiday.school === school.id,
              );
              const schoolGroups = groups.filter((group) => group.school === school.id);
              const schoolChildren = children.filter(
                (child) => child.school === school.id,
              );
              const schoolStudyDays = studyDays.filter(
                (studyDay) => studyDay.school === school.id,
              );
              return (
                <article key={school.id} className="school-card">
                  <div className="school-card-summary">
                    <span className="school-card-icon">
                      <GraduationCap size={18} />
                    </span>
                    <span>
                      <strong>{school.name}</strong>
                      <small>
                        {school.region
                          ? `Regio ${regionLabel(school.region)}`
                          : "Zelf ingevoerde vakanties"}
                      </small>
                    </span>
                    <div
                      className="school-card-metrics"
                      aria-label={`Samenvatting ${school.name}`}
                    >
                      <span>
                        <CalendarDays size={16} />
                        <strong>{schoolHolidays.length}</strong>
                        <small>Vakanties</small>
                      </span>
                      <span>
                        <UserRound size={16} />
                        <strong>{schoolChildren.length}</strong>
                        <small>Kinderen</small>
                      </span>
                      <span>
                        <Users size={16} />
                        <strong>{schoolGroups.length}</strong>
                        <small>Groepen</small>
                      </span>
                      <span>
                        <CalendarRange size={16} />
                        <strong>{schoolStudyDays.length}</strong>
                        <small>Studiedagen</small>
                      </span>
                    </div>
                    <span className="row-actions school-card-actions">
                      <button
                        className="icon-button"
                        type="button"
                        title="School wijzigen"
                        aria-label={`${school.name} wijzigen`}
                        onClick={() => startSchoolEdit(school)}
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        className="icon-button danger"
                        type="button"
                        title="School verwijderen"
                        aria-label={`${school.name} verwijderen`}
                        onClick={() => void removeSchool(school)}
                      >
                        <Trash2 size={15} />
                      </button>
                    </span>
                  </div>

                  <details className="school-block" open>
                    <div className="school-block-heading">
                      <span>
                        <CalendarDays size={18} />{" "}
                        <span>
                          <strong>Vakanties</strong>
                          <small>Houd je planning automatisch actueel.</small>
                        </span>
                      </span>
                      <span className="school-block-controls">
                        <button
                          className="text-action"
                          type="button"
                          aria-label="Vakantie toevoegen"
                          onClick={() => startHolidayEdit(school)}
                        >
                          <Plus size={14} /> Toevoegen
                        </button>
                        <ChevronDown className="school-block-chevron" size={17} />
                      </span>
                    </div>
                    <div className="school-block-content">
                      <div className="template-import">
                        <span>Regiotemplate</span>
                        <select
                          aria-label="Schooljaar voor vakantietemplate"
                          value={templateSchoolYear}
                          onChange={(event) => setTemplateSchoolYear(event.target.value)}
                        >
                          {schoolYearOptions.map((schoolYear) => (
                            <option key={schoolYear} value={schoolYear}>
                              {schoolYear}
                            </option>
                          ))}
                        </select>
                        <select
                          aria-label="Regio voor vakantietemplate"
                          value={templateSchoolId === school.id ? templateRegion : ""}
                          onChange={(event) => {
                            setTemplateSchoolId(school.id);
                            setTemplateRegion(
                              event.target.value as NonNullable<School["region"]>,
                            );
                          }}
                        >
                          <option value="">Kies Noord, Midden of Zuid</option>
                          <option value="north">Noord</option>
                          <option value="middle">Midden</option>
                          <option value="south">Zuid</option>
                        </select>
                        <button
                          className="button subtle"
                          type="button"
                          disabled={
                            working || templateSchoolId !== school.id || !templateRegion
                          }
                          onClick={() => void applyVacationTemplate(school)}
                        >
                          <Download size={15} /> Importeren
                        </button>
                      </div>
                      {schoolHolidays.length === 0 ? (
                        <p className="empty-copy">Nog geen vakanties toegevoegd.</p>
                      ) : (
                        schoolHolidays.map((holiday) => (
                          <div className="school-row" key={holiday.id}>
                            <span>
                              <strong>{holiday.name}</strong>
                              <small>
                                {formatPeriod(holiday.start_date, holiday.end_date)}
                              </small>
                            </span>
                            <span className="row-actions">
                              <button
                                className="icon-button"
                                type="button"
                                title="Vakantie wijzigen"
                                aria-label={`${holiday.name} wijzigen`}
                                onClick={() => startHolidayEdit(school, holiday)}
                              >
                                <Pencil size={14} />
                              </button>
                              <button
                                className="icon-button danger"
                                type="button"
                                title="Vakantie verwijderen"
                                aria-label={`${holiday.name} verwijderen`}
                                onClick={() => void removeHoliday(holiday)}
                              >
                                <Trash2 size={14} />
                              </button>
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </details>

                  <details className="school-block" open>
                    <div className="school-block-heading">
                      <span>
                        <Users size={18} />{" "}
                        <span>
                          <strong>Groepen</strong>
                          <small>
                            Maak eerst de groepen aan waaraan je kinderen koppelt.
                          </small>
                        </span>
                      </span>
                      <span className="school-block-controls">
                        <button
                          className="text-action"
                          type="button"
                          aria-label="Groep toevoegen"
                          onClick={() => startGroupEdit(school)}
                        >
                          <Plus size={14} /> Toevoegen
                        </button>
                        <ChevronDown className="school-block-chevron" size={17} />
                      </span>
                    </div>
                    <div className="school-block-content">
                      {schoolGroups.length === 0 ? (
                        <p className="empty-copy">Nog geen groepen toegevoegd.</p>
                      ) : (
                        schoolGroups.map((group) => (
                          <div className="school-row" key={group.id}>
                            <span>
                              <strong>{group.name}</strong>
                            </span>
                            <span className="row-actions">
                              <button
                                className="icon-button danger"
                                type="button"
                                title="Groep verwijderen"
                                aria-label={`${group.name} verwijderen`}
                                onClick={() => void removeGroup(group)}
                              >
                                <Trash2 size={14} />
                              </button>
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </details>

                  <details className="school-block" open>
                    <div className="school-block-heading">
                      <span>
                        <UserRound size={18} />{" "}
                        <span>
                          <strong>Kinderen</strong>
                          <small>Koppel ieder kind aan een groep.</small>
                        </span>
                      </span>
                      <span className="school-block-controls">
                        <button
                          className="text-action"
                          type="button"
                          aria-label="Kind toevoegen"
                          onClick={() => startChildEdit(school)}
                        >
                          <Plus size={14} /> Toevoegen
                        </button>
                        <ChevronDown className="school-block-chevron" size={17} />
                      </span>
                    </div>
                    <div className="school-block-content">
                      {schoolChildren.length === 0 ? (
                        <p className="empty-copy">Nog geen kinderen toegevoegd.</p>
                      ) : (
                        schoolChildren.map((child) => (
                          <div className="school-row" key={child.id}>
                            <span>
                              <strong>{child.name}</strong>
                              <small>{child.group_name}</small>
                            </span>
                            <span className="row-actions">
                              <button
                                className="icon-button danger"
                                type="button"
                                title="Kind verwijderen"
                                aria-label={`${child.name} verwijderen`}
                                onClick={() => void removeChild(child)}
                              >
                                <Trash2 size={14} />
                              </button>
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </details>

                  <details className="school-block" open>
                    <div className="school-block-heading">
                      <span>
                        <CalendarRange size={18} />{" "}
                        <span>
                          <strong>Studiedagen</strong>
                          <small>Plan dagen waarop de school gesloten is.</small>
                        </span>
                      </span>
                      <span className="school-block-controls">
                        <button
                          className="text-action"
                          type="button"
                          aria-label="Studiedag toevoegen"
                          onClick={() => startStudyDayEdit(school)}
                        >
                          <Plus size={14} /> Toevoegen
                        </button>
                        <ChevronDown className="school-block-chevron" size={17} />
                      </span>
                    </div>
                    <div className="school-block-content">
                      {schoolStudyDays.length === 0 ? (
                        <p className="empty-copy">Geen studiedagen ingevoerd.</p>
                      ) : (
                        schoolStudyDays.map((studyDay) => (
                          <div className="school-row" key={studyDay.id}>
                            <span>
                              <strong>{studyDay.name}</strong>
                              <small>
                                {formatDate(studyDay.date)} · {partLabel(studyDay.part)}
                                {studyDay.scope === "specific_groups"
                                  ? ` · ${studyDay.groups.length} groep(en)`
                                  : " · hele school"}
                              </small>
                            </span>
                            <span className="row-actions">
                              <button
                                className="icon-button"
                                type="button"
                                title="Studiedag wijzigen"
                                aria-label={`${studyDay.name} wijzigen`}
                                onClick={() => startStudyDayEdit(school, studyDay)}
                              >
                                <Pencil size={14} />
                              </button>
                              <button
                                className="icon-button danger"
                                type="button"
                                title="Studiedag verwijderen"
                                aria-label={`${studyDay.name} verwijderen`}
                                onClick={() => void removeStudyDay(studyDay)}
                              >
                                <Trash2 size={14} />
                              </button>
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </details>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {holidayDraft && (
        <div className="modal-backdrop school-vacations-modal" role="presentation">
          <form
            className="modal-panel school-vacations-editor"
            role="dialog"
            aria-modal="true"
            aria-labelledby="holiday-editor-title"
            onSubmit={saveHoliday}
          >
            <div className="settings-heading">
              <h2 id="holiday-editor-title">
                Vakantie voor {schoolName(schools, holidayDraft.school)}
              </h2>
            </div>
            <label>
              <span>Naam</span>
              <input
                value={holidayDraft.name}
                onChange={(event) =>
                  setHolidayDraft({ ...holidayDraft, name: event.target.value })
                }
                required
              />
            </label>
            <div className="form-row">
              <label>
                <span>Van</span>
                <DateField
                  value={holidayDraft.start_date}
                  onChange={(value) =>
                    setHolidayDraft({ ...holidayDraft, start_date: value })
                  }
                  required
                />
              </label>
              <label>
                <span>Tot en met</span>
                <DateField
                  value={holidayDraft.end_date}
                  min={holidayDraft.start_date}
                  onChange={(value) =>
                    setHolidayDraft({ ...holidayDraft, end_date: value })
                  }
                  required
                />
              </label>
            </div>
            <div className="form-actions">
              <button className="button primary" type="submit" disabled={working}>
                <Save size={16} /> Opslaan
              </button>
              <button
                className="button subtle"
                type="button"
                onClick={() => setHolidayDraft(null)}
              >
                <X size={16} /> Annuleren
              </button>
            </div>
          </form>
        </div>
      )}

      {groupDraft && (
        <div className="modal-backdrop school-vacations-modal" role="presentation">
          <form
            className="modal-panel school-vacations-editor"
            role="dialog"
            aria-modal="true"
            aria-labelledby="group-editor-title"
            onSubmit={saveGroup}
          >
            <div className="settings-heading">
              <h2 id="group-editor-title">
                Groep voor {schoolName(schools, groupDraft.school)}
              </h2>
            </div>
            <label>
              <span>Naam</span>
              <input
                value={groupDraft.name}
                placeholder="Bijvoorbeeld Groep 4 of Klas 2B"
                onChange={(event) =>
                  setGroupDraft({ ...groupDraft, name: event.target.value })
                }
                required
              />
            </label>
            <div className="form-actions">
              <button className="button primary" type="submit" disabled={working}>
                <Save size={16} /> Toevoegen
              </button>
              <button
                className="button subtle"
                type="button"
                onClick={() => setGroupDraft(null)}
              >
                <X size={16} /> Annuleren
              </button>
            </div>
          </form>
        </div>
      )}

      {childDraft && (
        <div className="modal-backdrop school-vacations-modal" role="presentation">
          <form
            className="modal-panel school-vacations-editor"
            role="dialog"
            aria-modal="true"
            aria-labelledby="child-editor-title"
            onSubmit={saveChild}
          >
            <div className="settings-heading">
              <h2 id="child-editor-title">
                Kind voor {schoolName(schools, childDraft.school)}
              </h2>
            </div>
            <label>
              <span>Naam</span>
              <input
                value={childDraft.name}
                onChange={(event) =>
                  setChildDraft({ ...childDraft, name: event.target.value })
                }
                required
              />
            </label>
            <div className="form-row">
              <label>
                <span>Groep / klas</span>
                <select
                  value={childDraft.group ?? ""}
                  onChange={(event) =>
                    setChildDraft({
                      ...childDraft,
                      group: event.target.value ? Number(event.target.value) : null,
                    })
                  }
                >
                  <option value="">Nieuwe groep aanmaken...</option>
                  {groups
                    .filter((group) => group.school === childDraft.school)
                    .map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                </select>
              </label>
              {!childDraft.group && (
                <label>
                  <span>Nieuwe groep</span>
                  <input
                    value={childDraft.new_group_name}
                    placeholder="Bijvoorbeeld Groep 4"
                    onChange={(event) =>
                      setChildDraft({
                        ...childDraft,
                        new_group_name: event.target.value,
                      })
                    }
                  />
                </label>
              )}
            </div>
            <div className="form-actions">
              <button className="button primary" type="submit" disabled={working}>
                <Save size={16} /> Toevoegen
              </button>
              <button
                className="button subtle"
                type="button"
                onClick={() => setChildDraft(null)}
              >
                <X size={16} /> Annuleren
              </button>
            </div>
          </form>
        </div>
      )}

      {studyDayDraft && (
        <div className="modal-backdrop school-vacations-modal" role="presentation">
          <form
            className="modal-panel school-vacations-editor"
            role="dialog"
            aria-modal="true"
            aria-labelledby="study-day-editor-title"
            onSubmit={saveStudyDay}
          >
            <div className="settings-heading">
              <h2 id="study-day-editor-title">
                Studiedag voor {schoolName(schools, studyDayDraft.school)}
              </h2>
            </div>
            <label>
              <span>Naam / omschrijving</span>
              <input
                value={studyDayDraft.name}
                onChange={(event) =>
                  setStudyDayDraft({ ...studyDayDraft, name: event.target.value })
                }
                required
              />
            </label>
            <div className="form-row">
              <label>
                <span>Datum</span>
                <DateField
                  value={studyDayDraft.date}
                  onChange={(value) =>
                    setStudyDayDraft({ ...studyDayDraft, date: value })
                  }
                  required
                />
              </label>
              <label>
                <span>Dagdeel</span>
                <select
                  value={studyDayDraft.part}
                  onChange={(event) =>
                    setStudyDayDraft({
                      ...studyDayDraft,
                      part: event.target.value as SchoolStudyDay["part"],
                    })
                  }
                >
                  <option value="whole_day">Hele dag</option>
                  <option value="morning">Ochtend</option>
                  <option value="afternoon">Middag</option>
                </select>
              </label>
            </div>
            <label>
              <span>Geldt voor</span>
              <select
                value={studyDayDraft.scope}
                onChange={(event) =>
                  setStudyDayDraft({
                    ...studyDayDraft,
                    scope: event.target.value as SchoolStudyDay["scope"],
                  })
                }
              >
                <option value="whole_school">Hele school</option>
                <option value="specific_groups">Eén of meerdere groepen</option>
              </select>
            </label>
            {studyDayDraft.scope === "specific_groups" && (
              <fieldset className="feed-options">
                <legend>Groepen</legend>
                {groups
                  .filter((group) => group.school === studyDayDraft.school)
                  .map((group) => (
                    <label key={group.id} className="check-field">
                      <input
                        type="checkbox"
                        checked={studyDayDraft.groups.includes(group.id)}
                        onChange={() => toggleStudyDayGroup(group.id)}
                      />
                      <span>{group.name}</span>
                    </label>
                  ))}
                {groups.filter((group) => group.school === studyDayDraft.school)
                  .length === 0 && (
                  <p className="empty-copy">
                    Voeg eerst een groep toe aan deze school.
                  </p>
                )}
              </fieldset>
            )}
            <div className="form-actions">
              <button className="button primary" type="submit" disabled={working}>
                <Save size={16} /> Opslaan
              </button>
              <button
                className="button subtle"
                type="button"
                onClick={() => setStudyDayDraft(null)}
              >
                <X size={16} /> Annuleren
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

function schoolName(schools: School[], id: number) {
  return schools.find((school) => school.id === id)?.name ?? "";
}

function regionLabel(region: NonNullable<School["region"]>) {
  return { north: "Noord", middle: "Midden", south: "Zuid" }[region];
}

function partLabel(part: SchoolStudyDay["part"]) {
  return { whole_day: "hele dag", morning: "ochtend", afternoon: "middag" }[part];
}

function formatPeriod(startDate: string, endDate: string) {
  if (startDate === endDate) return formatDate(startDate);
  return `${formatDate(startDate)} t/m ${formatDate(endDate)}`;
}

function uniqueSchoolYears(vacations: SchoolVacation[]) {
  return Array.from(new Set(vacations.map((vacation) => vacation.school_year))).sort(
    (left, right) => right.localeCompare(left),
  );
}
